import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../../shared/lib/apiClient';
import { useAuth } from '../../auth/context/AuthContext';
import { DEFAULT_TEMPLATE_CONFIG } from './InvoicePDF';
import quickbooksLogo from '../../../shared/assets/logos/integrations/quickbooks.svg';

const TABS = [
  { id: 'account', label: 'Profile', description: 'Your personal information' },
  { id: 'companies', label: 'Company', description: 'Company details and configuration' },
  { id: 'invoicing', label: 'Invoicing', description: 'Invoice column layout and defaults' },
  { id: 'operations', label: 'Operations', description: 'Integrations and tracking' },
  { id: 'data', label: 'Data & Analytics', description: 'Storage and sync preferences' },
  { id: 'payables', label: 'Accounts Payable', description: 'Email invoice ingestion' },
];

const SettingsView = ({ loaded }) => {
  const { user, profile, refreshProfile, companies, activeCompany, fetchCompanies } = useAuth();
  const [activeTab, setActiveTab] = useState('account');
  const [actingAsOpen, setActingAsOpen] = useState(false);
  const actingAsRef = useRef(null);

  const adminCompanies = companies.filter(
    (c) => c.role === 'owner' || c.role === 'admin'
  );

  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  useEffect(() => {
    if (selectedCompanyId) return;
    const activeMatch = adminCompanies.find((c) => c.companyId === activeCompany?.companyId);
    setSelectedCompanyId(activeMatch?.companyId || adminCompanies[0]?.companyId || null);
  }, [adminCompanies, activeCompany, selectedCompanyId]);

  const selectedCompany = adminCompanies.find((c) => c.companyId === selectedCompanyId);
  const showCompanyTabs = adminCompanies.length > 0;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (actingAsRef.current && !actingAsRef.current.contains(e.target)) {
        setActingAsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const visibleTabs = TABS.filter((t) => {
    if (t.id === 'invoicing') return false;
    if (t.id === 'companies' || t.id === 'operations' || t.id === 'data' || t.id === 'payables') return showCompanyTabs;
    return true;
  });

  return (
    <div className="max-w-[960px] mx-auto">
      {/* Header */}
      <div
        className="mb-5 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(12px)',
          transitionDelay: '300ms',
        }}
      >
        <div className="flex items-center justify-between">
          <h1 className="text-[22px] font-semibold text-zinc-900">Settings</h1>

          {/* Acting-as selector — inline in header */}
          {showCompanyTabs && activeTab !== 'account' && (
            <div className="relative" ref={actingAsRef}>
              <button
                onClick={() => setActingAsOpen(!actingAsOpen)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-zinc-200 hover:border-zinc-300 transition-colors text-left"
              >
                <span className="text-[13px] text-zinc-700 truncate max-w-[180px]">
                  {selectedCompany?.company?.name || 'Select'}
                </span>
                <span className="text-[10px] text-zinc-400 uppercase tracking-wider">
                  {selectedCompany?.role === 'owner' ? 'Owner' : 'Admin'}
                </span>
                <svg
                  width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5"
                  className={`text-zinc-400 shrink-0 transition-transform duration-200 ${actingAsOpen ? 'rotate-180' : ''}`}
                >
                  <path d="M3 5l3 3 3-3" />
                </svg>
              </button>

              {actingAsOpen && (
                  <div className="absolute right-0 mt-1 bg-white border border-zinc-200 rounded-md shadow-lg z-10 py-1 min-w-[200px]">
                    {adminCompanies.map((c) => (
                      <button
                        key={c.companyId}
                        onClick={() => {
                          setSelectedCompanyId(c.companyId);
                          setActingAsOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-[13px] transition-colors ${
                          c.companyId === selectedCompanyId
                            ? 'text-zinc-900 bg-zinc-50'
                            : 'text-zinc-500 hover:text-zinc-700 hover:bg-zinc-50'
                        }`}
                      >
                        <span className="block truncate">{c.company?.name || 'Unnamed'}</span>
                        <span className="text-[10px] text-zinc-400">
                          {c.role}
                          {c.companyId === activeCompany?.companyId ? ' (active)' : ''}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

      {/* Tab bar — underline style */}

      <div
        className="flex items-center border-b border-zinc-200 mb-6 overflow-x-auto scrollbar-hide transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(8px)',
          transitionDelay: '380ms',
        }}
      >
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-[13px] font-medium border-b-2 transition-all whitespace-nowrap -mb-px ${
              tab.id === activeTab
                ? 'border-zinc-900 text-zinc-900'
                : 'border-transparent text-zinc-400 hover:text-zinc-600'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div
        className="transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(8px)',
          transitionDelay: '440ms',
        }}
      >
        {activeTab === 'account' && (
          <AccountSettings user={user} profile={profile} refreshProfile={refreshProfile} activeCompany={activeCompany} companies={companies} />
        )}
        {activeTab === 'companies' && selectedCompany && (
          <CompanyForm
            key={selectedCompany.companyId}
            company={selectedCompany}
            loaded={loaded}
            onUpdated={fetchCompanies}
            allCompanies={companies}
          />
        )}
        {activeTab === 'invoicing' && selectedCompany && (
          <div className="space-y-6">
            <InvoicingSettings
              key={`inv-${selectedCompany.companyId}`}
              company={selectedCompany}
              onUpdated={fetchCompanies}
            />
            <TemplateManager
              key={`tpl-${selectedCompany.companyId}`}
              company={selectedCompany}
            />
          </div>
        )}
        {activeTab === 'operations' && selectedCompany && (
          <div className="space-y-4">
            <OperationsSettings
              key={`ops-${selectedCompany.companyId}`}
              company={selectedCompany}
              loaded={loaded}
              onUpdated={fetchCompanies}
            />
            <SyncPreferencesSettings
              key={`sync-${selectedCompany.companyId}`}
              company={selectedCompany}
              loaded={loaded}
            />
          </div>
        )}
        {activeTab === 'data' && selectedCompany && (
          <DataStorageConsent
            key={`consent-${selectedCompany.companyId}`}
            company={selectedCompany}
            loaded={loaded}
            onUpdated={fetchCompanies}
          />
        )}
        {activeTab === 'payables' && selectedCompany && (
          <AccountsPayable
            key={`ap-${selectedCompany.companyId}`}
            company={selectedCompany}
            loaded={loaded}
            onUpdated={fetchCompanies}
          />
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Account settings
// ---------------------------------------------------------------------------

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const avatarColorSettings = (name) => {
  const colors = [
    'bg-violet-100 text-violet-700',
    'bg-blue-100 text-blue-700',
    'bg-emerald-100 text-emerald-700',
    'bg-amber-100 text-amber-700',
    'bg-rose-100 text-rose-700',
  ];
  const i = (name || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return colors[i % colors.length];
};

const AccountSettings = ({ user, profile, refreshProfile, activeCompany, companies }) => {
  const [fullName, setFullName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    setFullName(profile?.full_name || '');
  }, [profile]);

  const handleSave = async () => {
    if (!fullName.trim()) return;
    setSaving(true);
    setError('');
    setSaved(false);

    try {
      const res = await apiFetch('/api/user/profile', {
        method: 'PUT',
        body: JSON.stringify({ full_name: fullName.trim() }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update');
      }
      setSaved(true);
      refreshProfile?.();
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setAvatarError('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setAvatarError('Image must be under 5MB');
      return;
    }

    setUploading(true);
    setAvatarError('');

    try {
      const uploadRes = await apiFetch('/api/user/avatar/upload', { method: 'POST' });
      if (!uploadRes.ok) throw new Error('Failed to get upload URL');
      const { uploadUrl, fields, fileKey } = await uploadRes.json();

      const formData = new FormData();
      Object.entries(fields).forEach(([k, v]) => formData.append(k, v));
      formData.set('Content-Type', file.type);
      formData.append('file', file);

      const s3Res = await fetch(uploadUrl, { method: 'POST', body: formData });
      if (!s3Res.ok) throw new Error(`Upload failed (${s3Res.status})`);

      const saveRes = await apiFetch('/api/user/profile', {
        method: 'PUT',
        body: JSON.stringify({ avatar_url: fileKey }),
      });
      if (!saveRes.ok) throw new Error('Failed to save avatar');

      refreshProfile?.();
    } catch (err) {
      setAvatarError(err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveAvatar = async () => {
    setUploading(true);
    setAvatarError('');
    try {
      const res = await apiFetch('/api/user/avatar', { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to remove avatar');
      refreshProfile?.();
    } catch (err) {
      setAvatarError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const hasChanges = fullName.trim() !== (profile?.full_name || '');
  const displayName = profile?.full_name || user?.email?.split('@')[0] || '';
  const memberSince = profile?.created_at ? new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : null;

  const ROLE_LABELS = { owner: 'Owner', admin: 'Admin', member: 'Member' };

  return (
    <div className="space-y-5">
      {/* Profile card with avatar */}
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
          <h2 className="text-[14px] font-semibold text-zinc-900">Personal Information</h2>
          {memberSince && <span className="text-[11px] text-zinc-400">Member since {memberSince}</span>}
        </div>

        <div className="p-5">
          {/* Avatar + identity row */}
          <div className="flex items-start gap-5 pb-5 border-b border-zinc-100 mb-5">
            <div className="relative group">
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt="Avatar" className="w-20 h-20 rounded-full object-cover" />
              ) : (
                <div className={`w-20 h-20 rounded-full flex items-center justify-center text-xl font-semibold ${avatarColorSettings(displayName)}`}>
                  {getInitials(displayName)}
                </div>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
              </button>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleAvatarUpload} className="hidden" />
            </div>
            <div className="flex-1 min-w-0 pt-1">
              <h3 className="text-[16px] font-semibold text-zinc-900">{displayName || 'Unnamed'}</h3>
              <p className="text-[13px] text-zinc-400 mt-0.5">{user?.email || ''}</p>
              {activeCompany && (
                <div className="flex items-center gap-2 mt-2">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-medium ${
                    activeCompany.role === 'owner' ? 'bg-zinc-900 text-white' : activeCompany.role === 'admin' ? 'bg-zinc-200 text-zinc-700' : 'bg-zinc-100 text-zinc-500'
                  }`}>
                    {ROLE_LABELS[activeCompany.role] || activeCompany.role}
                  </span>
                  <span className="text-[12px] text-zinc-400">at {activeCompany.company?.name}</span>
                </div>
              )}
              <div className="flex items-center gap-2 mt-3">
                {profile?.avatar_url && (
                  <button onClick={handleRemoveAvatar} disabled={uploading}
                    className="text-[11px] text-red-500 hover:text-red-600 transition-colors disabled:opacity-40">
                    Remove photo
                  </button>
                )}
              </div>
            </div>
          </div>
          {avatarError && <p className="text-[12px] text-red-500 mb-4">{avatarError}</p>}

          {/* Form fields in grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-zinc-400 font-medium mb-1.5">Full Name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Your name"
                className="w-full px-3 py-2 border border-zinc-200 text-[13px] text-zinc-900 rounded-md outline-none focus:border-zinc-400 transition-colors"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-zinc-400 font-medium mb-1.5">Email Address</label>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 text-[13px] text-zinc-400 rounded-md cursor-not-allowed"
              />
              <p className="text-[10px] text-zinc-300 mt-1">Managed by authentication provider</p>
            </div>
            {activeCompany?.jobTitle && (
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-zinc-400 font-medium mb-1.5">Job Title</label>
                <p className="px-3 py-2 text-[13px] text-zinc-700">{activeCompany.jobTitle}</p>
              </div>
            )}
            {activeCompany?.department && (
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-zinc-400 font-medium mb-1.5">Department</label>
                <p className="px-3 py-2 text-[13px] text-zinc-700">{activeCompany.department}</p>
              </div>
            )}
          </div>

          {error && <p className="text-[12px] text-red-500 mt-3">{error}</p>}

          <div className="flex items-center gap-3 mt-5 pt-4 border-t border-zinc-100">
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="px-4 py-2 bg-zinc-900 text-white text-[12px] font-medium rounded-md hover:bg-zinc-800 transition-colors disabled:opacity-40 flex items-center gap-2"
            >
              {saving && <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            {saved && <span className="text-[12px] text-emerald-600">Saved successfully</span>}
          </div>
        </div>
      </div>

      {/* Workspaces card */}
      {companies && companies.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-zinc-200">
            <h2 className="text-[14px] font-semibold text-zinc-900">Workspaces</h2>
          </div>
          <div className="divide-y divide-zinc-100">
            {companies.map((c) => (
              <div key={c.companyId} className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-md bg-zinc-100 flex items-center justify-center text-[11px] font-semibold text-zinc-500 shrink-0">
                    {(c.company?.name || '?')[0].toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[13px] font-medium text-zinc-900 truncate">{c.company?.name || 'Unnamed'}</p>
                    {c.jobTitle && <p className="text-[11px] text-zinc-400 truncate">{c.jobTitle}{c.department ? ` · ${c.department}` : ''}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-medium ${
                    c.role === 'owner' ? 'bg-zinc-900 text-white' : c.role === 'admin' ? 'bg-zinc-200 text-zinc-700' : 'bg-zinc-100 text-zinc-500'
                  }`}>
                    {ROLE_LABELS[c.role] || c.role}
                  </span>
                  {c.companyId === activeCompany?.companyId && (
                    <span className="text-[10px] text-emerald-600 font-medium">Active</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Company form (edits a single company)
// ---------------------------------------------------------------------------

const INDUSTRY_OPTIONS = [
  '', 'Restaurant', 'Bar & Nightlife', 'Cafe & Coffee', 'Hotel & Hospitality',
  'Catering', 'Food Truck', 'Retail', 'Healthcare', 'Technology', 'Other',
];

const SIZE_OPTIONS = [
  '', '1-10', '11-50', '51-200', '201-500', '500+',
];

const CompanyForm = ({ company, loaded, onUpdated, allCompanies }) => {
  const navigate = useNavigate();
  const [name, setName] = useState(company.company?.name || '');
  const [industry, setIndustry] = useState(company.company?.industry || '');
  const [size, setSize] = useState(company.company?.size || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const original = company.company || {};
  const hasChanges =
    name.trim() !== (original.name || '') ||
    industry !== (original.industry || '') ||
    size !== (original.size || '');

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError('');
    setSaved(false);

    try {
      const res = await apiFetch('/api/companies', {
        method: 'PUT',
        body: JSON.stringify({ name: name.trim(), industry, size }),
        companyId: company.companyId,
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update');
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      if (onUpdated) onUpdated();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="bg-white border border-zinc-100 rounded-lg overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
      style={{
        opacity: loaded ? 1 : 0,
        transform: loaded ? 'translateY(0)' : 'translateY(12px)',
        transitionDelay: '600ms',
      }}
    >
      <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-zinc-100 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-medium text-zinc-900">
            {company.company?.name || 'Company'} Settings
          </h2>
          <p className="text-[11px] text-zinc-400 mt-0.5">Company details and configuration</p>
        </div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
          company.role === 'owner' ? 'bg-zinc-900 text-white' : 'bg-zinc-200 text-zinc-700'
        }`}>
          {company.role}
        </span>
      </div>

      <div className="p-4 sm:p-5 space-y-5">
        {/* Company name */}
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-1.5">
            Company Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Company name"
            className="w-full px-4 py-2.5 border border-zinc-200 text-sm text-zinc-900 rounded-md outline-none focus:border-zinc-900 transition-colors duration-300"
          />
        </div>

        {/* Industry */}
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-1.5">
            Industry
          </label>
          <select
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            className="w-full px-4 py-2.5 border border-zinc-200 text-sm text-zinc-900 rounded-md outline-none focus:border-zinc-900 transition-colors duration-300 bg-white appearance-none cursor-pointer"
          >
            {INDUSTRY_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt || 'Select industry...'}
              </option>
            ))}
          </select>
        </div>

        {/* Company size */}
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-1.5">
            Company Size
          </label>
          <select
            value={size}
            onChange={(e) => setSize(e.target.value)}
            className="w-full px-4 py-2.5 border border-zinc-200 text-sm text-zinc-900 rounded-md outline-none focus:border-zinc-900 transition-colors duration-300 bg-white appearance-none cursor-pointer"
          >
            {SIZE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt || 'Select size...'}
              </option>
            ))}
          </select>
        </div>

        {/* Company ID (read-only) */}
        <div>
        </div>

        {error && (
          <p className="text-sm text-red-500">{error}</p>
        )}

        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="px-5 py-2 bg-zinc-900 text-white text-xs tracking-wider uppercase rounded-md hover:bg-zinc-800 transition-colors disabled:opacity-40 flex items-center gap-2"
          >
            {saving && (
              <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
            )}
            {saving ? 'Saving...' : 'Save'}
          </button>
          {saved && (
            <span className="text-sm text-emerald-600">Saved</span>
          )}
        </div>
      </div>

      {/* Danger zone — owner only */}
      {company.role === 'owner' && (
        <div className="px-4 sm:px-5 py-4 border-t border-red-100 bg-red-50/30">
          {!showDeleteConfirm ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[13px] font-medium text-red-600">Delete this company</p>
                <p className="text-[11px] text-red-400">This will permanently delete all data, members, and integrations.</p>
              </div>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-3 py-1.5 border border-red-200 text-red-600 text-[12px] font-medium rounded-md hover:bg-red-50 transition-colors"
              >
                Delete
              </button>
            </div>
          ) : (
            <div>
              <p className="text-[13px] font-medium text-red-600 mb-2">
                Type <span className="font-mono bg-red-100 px-1.5 py-0.5 rounded text-red-700">{company.company?.name}</span> to confirm
              </p>
              <input
                type="text"
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder="Company name"
                className="w-full px-3 py-2 border border-red-200 text-[13px] text-zinc-900 rounded-md outline-none focus:border-red-400 transition-colors mb-3"
              />
              {deleteError && <p className="text-[12px] text-red-500 mb-2">{deleteError}</p>}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmName(''); setDeleteError(''); }}
                  className="px-3 py-1.5 border border-zinc-200 text-zinc-600 text-[12px] font-medium rounded-md hover:bg-zinc-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (deleteConfirmName !== company.company?.name) {
                      setDeleteError('Company name doesn\'t match.');
                      return;
                    }
                    setDeleting(true);
                    setDeleteError('');
                    try {
                      const res = await apiFetch('/api/companies', { method: 'DELETE' });
                      if (!res.ok) {
                        const data = await res.json();
                        throw new Error(data.error || 'Failed to delete');
                      }
                      // Find remaining companies (exclude the one being deleted)
                      const remaining = (allCompanies || []).filter(c => c.companyId !== company.companyId);
                      if (remaining.length > 0) {
                        // Switch to earliest created company
                        const next = remaining.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0))[0];
                        localStorage.setItem('active_company_id', next.companyId);
                      } else {
                        localStorage.removeItem('active_company_id');
                      }
                      await onUpdated?.();
                      navigate(remaining.length > 0 ? '/dashboard' : '/onboarding');
                    } catch (err) {
                      setDeleteError(err.message);
                      setDeleting(false);
                    }
                  }}
                  disabled={deleting || deleteConfirmName !== company.company?.name}
                  className="px-3 py-1.5 bg-red-600 text-white text-[12px] font-medium rounded-md hover:bg-red-700 transition-colors disabled:opacity-40 flex items-center gap-2"
                >
                  {deleting && <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  {deleting ? 'Deleting...' : 'Permanently Delete'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Data storage consent
// ---------------------------------------------------------------------------

const DataStorageConsent = ({ company, loaded, onUpdated }) => {
  const [enabled, setEnabled] = useState(!!company.company?.data_storage_consent);
  const [saving, setSaving] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [advancedAnalyticsEnabled, setAdvancedAnalyticsEnabled] = useState(false);
  const [advancedAnalyticsStatus, setAdvancedAnalyticsStatus] = useState('not_started');
  const [advancedSaving, setAdvancedSaving] = useState(false);
  const [advancedError, setAdvancedError] = useState('');

  useEffect(() => {
    let active = true;

    (async () => {
      setAdvancedError('');
      try {
        const res = await apiFetch('/api/companies/qb-sync-settings', {
          companyId: company.companyId,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || 'Failed to load Advanced Data & Analytics setting');
        }
        if (!active) return;
        setAdvancedAnalyticsEnabled(!!data.settings?.data_sync_enabled);
        setAdvancedAnalyticsStatus(data.settings?.quickbooks_sync_status || 'not_started');
      } catch (err) {
        if (!active) return;
        setAdvancedError(err.message || 'Failed to load Advanced Data & Analytics setting');
      }
    })();

    return () => {
      active = false;
    };
  }, [company.companyId]);

  const handleToggle = async () => {
    const next = !enabled;
    setSaving(true);
    setSyncStatus(null);
    setSyncResult(null);

    try {
      const res = await apiFetch('/api/companies', {
        method: 'PUT',
        body: JSON.stringify({ data_storage_consent: next }),
        companyId: company.companyId,
      });
      if (!res.ok) throw new Error('Failed to update');
      setEnabled(next);
      if (onUpdated) onUpdated();

      if (next) {
        setSyncStatus('syncing');
        try {
          const syncRes = await apiFetch('/api/vendors/sync/quickbooks', {
            method: 'POST',
            companyId: company.companyId,
          });
          if (syncRes.ok) {
            const data = await syncRes.json();
            setSyncResult(data);
            setSyncStatus('done');
          } else {
            setSyncStatus(null);
          }
        } catch {
          setSyncStatus(null);
        }
      }
    } catch {
      // revert on failure
    } finally {
      setSaving(false);
    }
  };

  const handleAdvancedAnalyticsToggle = async () => {
    const next = !advancedAnalyticsEnabled;
    setAdvancedSaving(true);
    setAdvancedError('');

    try {
      const res = await apiFetch('/api/companies/qb-sync-settings', {
        method: 'PUT',
        body: JSON.stringify({ data_sync_enabled: next }),
        companyId: company.companyId,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to update Advanced Data & Analytics setting');
      }

      setAdvancedAnalyticsEnabled(!!data.settings?.data_sync_enabled);
      setAdvancedAnalyticsStatus(data.settings?.quickbooks_sync_status || 'queued');
    } catch (err) {
      setAdvancedError(err.message || 'Failed to update Advanced Data & Analytics setting');
    } finally {
      setAdvancedSaving(false);
    }
  };

  return (
    <div
      className="bg-white border border-zinc-100 rounded-lg overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
      style={{
        opacity: loaded ? 1 : 0,
        transform: loaded ? 'translateY(0)' : 'translateY(12px)',
        transitionDelay: '700ms',
      }}
    >
      <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-zinc-100">
        <h2 className="text-sm font-medium text-zinc-900">Data & Analytics</h2>
        <p className="text-[11px] text-zinc-400 mt-0.5">Storage and sync preferences</p>
      </div>

      <div className="p-4 sm:p-5">
        <div className="flex items-start gap-4">
          <button
            onClick={handleToggle}
            disabled={saving || syncStatus === 'syncing'}
            className={`mt-0.5 relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50 ${
              enabled ? 'bg-zinc-900' : 'bg-zinc-200'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition-transform duration-200 ease-in-out mt-0.5 ${
                enabled ? 'translate-x-4 ml-0.5' : 'translate-x-0 ml-0.5'
              }`}
            />
          </button>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-zinc-900">
              Store data locally for enhanced analytics
            </p>
            <p className="text-sm text-zinc-400 mt-1 leading-relaxed">
              Allow Arbcor to securely store a copy of your synced data to power cross-platform analytics,
              trend insights, and reporting not available through any single integration alone.
              Your data is encrypted at rest and never shared with third parties.
            </p>

            {syncStatus === 'syncing' && (
              <div className="mt-3 flex items-center gap-2">
                <div className="w-3 h-3 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin shrink-0" />
                <span className="text-xs text-zinc-500">Syncing data from connected integrations...</span>
              </div>
            )}

            {syncStatus === 'done' && syncResult && (
              <div className="mt-3 flex items-center gap-2">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-500 shrink-0">
                  <path d="M4 8.5L7 11.5L12 4.5" />
                </svg>
                <span className="text-xs text-emerald-600">
                  Initial sync complete — {syncResult.created} new, {syncResult.updated} updated, {syncResult.skipped} unchanged
                </span>
              </div>
            )}

            {enabled && !syncStatus && (
              <div className="mt-3 flex items-center gap-2">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-500 shrink-0">
                  <path d="M4 8.5L7 11.5L12 4.5" />
                </svg>
                <span className="text-xs text-emerald-600">Enabled — Arbcor will store and analyze your data</span>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Operations settings
// ---------------------------------------------------------------------------

const OperationsSettings = ({ company, loaded, onUpdated }) => {
  const [qbLoading, setQbLoading] = useState(true);
  const [qbConnected, setQbConnected] = useState(false);
  const [qbCompanyName, setQbCompanyName] = useState('');
  const [qbActionLoading, setQbActionLoading] = useState(false);
  const [qbError, setQbError] = useState('');

  const [trackLocations, setTrackLocations] = useState(!!company.company?.track_locations);
  const [trackSaving, setTrackSaving] = useState(false);
  const [trackError, setTrackError] = useState('');

  useEffect(() => {
    setTrackLocations(!!company.company?.track_locations);
  }, [company.company?.track_locations]);

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const res = await apiFetch('/api/quickbooks/status', { companyId: company.companyId });
        if (!res.ok || !active) return;

        const data = await res.json();
        setQbConnected(!!data.connected);
        setQbCompanyName(data.companyName || '');
      } catch {
        // silent
      } finally {
        if (active) setQbLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [company.companyId]);

  const handleQuickBooksConnect = async () => {
    setQbActionLoading(true);
    setQbError('');

    try {
      const response = await apiFetch('/api/quickbooks/authorize', { companyId: company.companyId });
      const data = await response.json();
      if (data.success && data.authUrl) {
        window.location.href = data.authUrl;
        return;
      }
      setQbError(data.message || 'Failed to start QuickBooks connection');
    } catch {
      setQbError('Failed to connect to QuickBooks');
    } finally {
      setQbActionLoading(false);
    }
  };

  const handleQuickBooksDisconnect = async () => {
    setQbActionLoading(true);
    setQbError('');

    try {
      const response = await apiFetch('/api/quickbooks/disconnect', {
        method: 'POST',
        companyId: company.companyId,
      });
      const data = await response.json();
      if (!data.success) throw new Error('Failed to disconnect');

      setQbConnected(false);
      setQbCompanyName('');
    } catch {
      setQbError('Failed to disconnect QuickBooks');
    } finally {
      setQbActionLoading(false);
    }
  };

  const handleTrackLocationsToggle = async () => {
    const next = !trackLocations;
    setTrackSaving(true);
    setTrackError('');

    try {
      const res = await apiFetch('/api/companies', {
        method: 'PUT',
        body: JSON.stringify({ track_locations: next }),
        companyId: company.companyId,
      });
      if (!res.ok) throw new Error('Failed to update location tracking');

      setTrackLocations(next);
      if (onUpdated) onUpdated();
    } catch (err) {
      setTrackError(err.message || 'Failed to update location tracking');
    } finally {
      setTrackSaving(false);
    }
  };

  return (
    <div
      className="bg-white border border-zinc-100 rounded-lg overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
      style={{
        opacity: loaded ? 1 : 0,
        transform: loaded ? 'translateY(0)' : 'translateY(12px)',
        transitionDelay: '800ms',
      }}
    >
      <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-zinc-100">
        <h2 className="text-sm font-medium text-zinc-900">Operations</h2>
        <p className="text-[11px] text-zinc-400 mt-0.5">Integrations and tracking</p>
      </div>

      <div className="p-4 sm:p-5 space-y-5">
        <div className="flex items-start gap-4">
          <button
            onClick={handleTrackLocationsToggle}
            disabled={trackSaving}
            className={`mt-0.5 relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50 ${
              trackLocations ? 'bg-zinc-900' : 'bg-zinc-200'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition-transform duration-200 ease-in-out mt-0.5 ${
                trackLocations ? 'translate-x-4 ml-0.5' : 'translate-x-0 ml-0.5'
              }`}
            />
          </button>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-zinc-900">Track locations</p>
            <p className="text-sm text-zinc-400 mt-1">
              Enable location-level tracking so analytics can be segmented by store, branch, or site.
            </p>
            {trackError && (
              <p className="text-xs text-red-500 mt-2">{trackError}</p>
            )}
            {!trackError && (
              <p className={`text-xs mt-2 ${trackLocations ? 'text-emerald-600' : 'text-zinc-500'}`}>
                {trackLocations ? 'Enabled' : 'Disabled'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sync Preferences — pull/push settings per entity
// ---------------------------------------------------------------------------

const SYNC_ENTITIES = [
  { entity: 'vendors', label: 'Vendors', pullLabel: 'Import vendors', pushLabel: 'Send new vendors' },
  { entity: 'bills', label: 'Bills', pullLabel: 'Import bills', pushLabel: 'Send new bills' },
  { entity: 'payments', label: 'Payments', pullLabel: 'Import payments', pushLabel: null },
  { entity: 'purchase_orders', label: 'Purchase Orders', pullLabel: 'Import purchase orders', pushLabel: null },
];

const SyncPreferencesSettings = ({ company, loaded }) => {
  const [prefs, setPrefs] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [intRes, prefRes] = await Promise.all([
          apiFetch('/api/integrations').then(r => r.json()).catch(() => ({ integrations: [] })),
          apiFetch('/api/sync/preferences').then(r => r.json()).catch(() => ({ preferences: [] })),
        ]);
        if (!active) return;
        const connected = (intRes.integrations || []).filter(i => i.status === 'active');
        setIntegrations(connected);
        setPrefs(prefRes.preferences || []);
      } catch { /* silent */ }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, []);

  const isEnabled = (provider, entity) => !!prefs.find(p => p.entity === entity && p.provider === provider)?.enabled;

  const toggle = async (provider, entity) => {
    const current = isEnabled(provider, entity);
    const key = `${provider}:${entity}`;
    setSaving(key);
    try {
      await apiFetch('/api/sync/preferences', {
        method: 'PUT',
        body: JSON.stringify({ provider, entity, enabled: !current }),
      });
      setPrefs(prev => {
        const others = prev.filter(p => !(p.entity === entity && p.provider === provider));
        return [...others, { provider, entity, enabled: !current }];
      });
    } catch (err) { console.error('Sync pref save failed:', err); }
    setSaving(null);
  };

  if (integrations.length === 0) return null;

  const providerName = (id) => ({ quickbooks: 'QuickBooks', xero: 'Xero', plaid: 'Plaid' }[id] || id);

  return (
    <div
      className="bg-white border border-zinc-100 rounded-lg overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
      style={{
        opacity: loaded ? 1 : 0,
        transform: loaded ? 'translateY(0)' : 'translateY(12px)',
        transitionDelay: '850ms',
      }}
    >
      <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-zinc-100">
        <h2 className="text-sm font-medium text-zinc-900">Sync Preferences</h2>
        <p className="text-[11px] text-zinc-400 mt-0.5">Control what data flows between Arbcor and your connected integrations</p>
      </div>

      {integrations.map((int) => (
        <div key={int.provider}>
          <div className="px-4 sm:px-5 py-2 bg-zinc-50 border-b border-zinc-100">
            <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-medium">{providerName(int.provider)}</span>
          </div>
          <div className="divide-y divide-zinc-100">
            {SYNC_ENTITIES.map(({ entity, label, pullLabel, pushLabel }) => {
              const pullKey = `${int.provider}:${entity}`;
              const pushKey = `${int.provider}:${entity}_push`;
              return (
                <div key={entity} className="px-4 sm:px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
                  <span className="text-sm font-medium text-zinc-900 sm:w-36 shrink-0">{label}</span>
                  <div className="flex items-center gap-5 flex-1">
                    {/* Pull */}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <button
                        onClick={() => toggle(int.provider, entity)}
                        disabled={saving === pullKey || loading}
                        className={`relative inline-flex h-4 w-7 shrink-0 rounded-full transition-colors duration-200 disabled:opacity-50 ${isEnabled(int.provider, entity) ? 'bg-zinc-900' : 'bg-zinc-200'}`}
                      >
                        <span className={`inline-block h-3 w-3 rounded-full bg-white shadow transform transition-transform duration-200 mt-0.5 ${isEnabled(int.provider, entity) ? 'translate-x-3 ml-0.5' : 'translate-x-0 ml-0.5'}`} />
                      </button>
                      <span className="text-[12px] text-zinc-500">{pullLabel}</span>
                    </label>
                    {/* Push */}
                    {pushLabel && (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <button
                          onClick={() => toggle(int.provider, `${entity}_push`)}
                          disabled={saving === pushKey || loading}
                          className={`relative inline-flex h-4 w-7 shrink-0 rounded-full transition-colors duration-200 disabled:opacity-50 ${isEnabled(int.provider, `${entity}_push`) ? 'bg-zinc-900' : 'bg-zinc-200'}`}
                        >
                          <span className={`inline-block h-3 w-3 rounded-full bg-white shadow transform transition-transform duration-200 mt-0.5 ${isEnabled(int.provider, `${entity}_push`) ? 'translate-x-3 ml-0.5' : 'translate-x-0 ml-0.5'}`} />
                        </button>
                        <span className="text-[12px] text-zinc-500">{pushLabel}</span>
                      </label>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Accounts Payable — email ingest
// ---------------------------------------------------------------------------

const slugify = (name) =>
  (name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const AP_DOMAIN = 'arbcor.com';

const AccountsPayable = ({ company, loaded, onUpdated }) => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [slug, setSlug] = useState(company.company?.email_slug || '');
  const [slugInput, setSlugInput] = useState(company.company?.email_slug || '');
  const [saving, setSaving] = useState(false);
  const [slugSaving, setSlugSaving] = useState(false);
  const [slugStatus, setSlugStatus] = useState(null);
  const [copied, setCopied] = useState(false);
  const slugTimerRef = useState(null);

  const fullEmail = slug ? `payables@${slug}.${AP_DOMAIN}` : null;

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/api/ingest-emails', { companyId: company.companyId });
        if (res.ok) {
          const data = await res.json();
          const invoices = (data.emails || []).find((e) => e.type === 'invoices');
          if (invoices) setEnabled(!!invoices.enabled);
        }
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    })();
  }, [company.companyId]);

  const handleToggle = async () => {
    const next = !enabled;
    setSaving(true);

    if (next && !slug) {
      const defaultSlug = slugify(company.company?.name);
      if (defaultSlug) {
        try {
          const slugRes = await apiFetch('/api/companies', {
            method: 'PUT',
            body: JSON.stringify({ email_slug: defaultSlug }),
            companyId: company.companyId,
          });
          if (slugRes.ok) {
            setSlug(defaultSlug);
            setSlugInput(defaultSlug);
            if (onUpdated) onUpdated();
          }
        } catch {
          // slug conflict or error
        }
      }
    }

    try {
      const res = await apiFetch('/api/ingest-emails', {
        method: 'PUT',
        body: JSON.stringify({ type: 'invoices', enabled: next }),
        companyId: company.companyId,
      });
      if (!res.ok) throw new Error('Failed to update');
      setEnabled(next);
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  };

  const handleSlugChange = (val) => {
    const clean = val.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSlugInput(clean);
    setSlugStatus(null);

    if (slugTimerRef[0]) clearTimeout(slugTimerRef[0]);
    if (!clean || clean === slug) return;

    slugTimerRef[0] = setTimeout(async () => {
      setSlugStatus('checking');
      try {
        const res = await apiFetch(`/api/companies/slug-available?slug=${clean}&companyId=${company.companyId}`);
        const data = await res.json();
        setSlugStatus(data.available ? 'available' : 'taken');
      } catch {
        setSlugStatus(null);
      }
    }, 400);
  };

  const handleSaveSlug = async () => {
    if (!slugInput || slugStatus === 'taken') return;
    setSlugSaving(true);

    try {
      const res = await apiFetch('/api/companies', {
        method: 'PUT',
        body: JSON.stringify({ email_slug: slugInput }),
        companyId: company.companyId,
      });
      if (!res.ok) {
        const data = await res.json();
        if (res.status === 409) {
          setSlugStatus('taken');
          return;
        }
        throw new Error(data.error);
      }
      setSlug(slugInput);
      setSlugStatus('saved');
      setTimeout(() => setSlugStatus(null), 2000);
      if (onUpdated) onUpdated();
    } catch {
      // silent
    } finally {
      setSlugSaving(false);
    }
  };

  const handleCopy = () => {
    if (!fullEmail) return;
    navigator.clipboard.writeText(fullEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className="bg-white border border-zinc-100 rounded-lg overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
      style={{
        opacity: loaded ? 1 : 0,
        transform: loaded ? 'translateY(0)' : 'translateY(12px)',
        transitionDelay: '900ms',
      }}
    >
      <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-zinc-100">
        <h2 className="text-sm font-medium text-zinc-900">Accounts Payable</h2>
        <p className="text-[11px] text-zinc-400 mt-0.5">Email invoice ingestion</p>
      </div>

      <div className="p-4 sm:p-5">
        <div className="flex items-start gap-4">
          <button
            onClick={handleToggle}
            disabled={saving || loading}
            className={`mt-0.5 relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50 ${
              enabled ? 'bg-zinc-900' : 'bg-zinc-200'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition-transform duration-200 ease-in-out mt-0.5 ${
                enabled ? 'translate-x-4 ml-0.5' : 'translate-x-0 ml-0.5'
              }`}
            />
          </button>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-zinc-900">
              Email invoice ingestion
            </p>
            <p className="text-sm text-zinc-400 mt-1 leading-relaxed">
              Generate a dedicated email address for receiving invoices. Forward or share it with your vendors —
              any PDF invoices sent there will be automatically extracted, matched to vendors, and added to your invoice list.
            </p>

            {enabled && (
              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-1.5">
                    Email Address
                  </label>
                  <div className="flex items-center gap-0">
                    <span className="px-2 sm:px-3 py-2 bg-zinc-50 border border-r-0 border-zinc-200 text-xs sm:text-sm text-zinc-400 rounded-l-md shrink-0">
                      payables@
                    </span>
                    <input
                      type="text"
                      value={slugInput}
                      onChange={(e) => handleSlugChange(e.target.value)}
                      placeholder="company-name"
                      className="flex-1 px-2 sm:px-3 py-2 border border-zinc-200 text-xs sm:text-sm text-zinc-900 outline-none focus:border-zinc-900 transition-colors duration-300 min-w-0"
                    />
                    <span className="px-2 sm:px-3 py-2 bg-zinc-50 border border-l-0 border-zinc-200 text-xs sm:text-sm text-zinc-400 rounded-r-md shrink-0">
                      .{AP_DOMAIN}
                    </span>
                  </div>

                  {slugStatus === 'checking' && (
                    <p className="text-[10px] text-zinc-400 mt-1 flex items-center gap-1">
                      <span className="w-2 h-2 border border-zinc-300 border-t-zinc-600 rounded-full animate-spin inline-block" />
                      Checking availability...
                    </p>
                  )}
                  {slugStatus === 'available' && (
                    <p className="text-[10px] text-emerald-600 mt-1">Available</p>
                  )}
                  {slugStatus === 'taken' && (
                    <p className="text-[10px] text-red-500 mt-1">This address is already taken</p>
                  )}
                  {slugStatus === 'saved' && (
                    <p className="text-[10px] text-emerald-600 mt-1">Saved</p>
                  )}

                  {slugInput && slugInput !== slug && slugStatus !== 'taken' && (
                    <button
                      onClick={handleSaveSlug}
                      disabled={slugSaving || slugStatus === 'checking'}
                      className="mt-2 px-4 py-1.5 bg-zinc-900 text-white text-[10px] uppercase tracking-[0.15em] rounded hover:bg-zinc-800 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {slugSaving && (
                        <span className="w-2.5 h-2.5 border border-white border-t-transparent rounded-full animate-spin inline-block" />
                      )}
                      {slugSaving ? 'Saving...' : 'Save'}
                    </button>
                  )}
                </div>

                {fullEmail && (
                  <div className="flex items-center gap-2 bg-zinc-50 rounded-md px-4 py-3">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-400 shrink-0" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="1" y="3" width="14" height="10" rx="1" />
                      <path d="M1 4l7 5 7-5" />
                    </svg>
                    <span className="text-sm font-mono text-zinc-700 flex-1 truncate">{fullEmail}</span>
                    <button
                      onClick={handleCopy}
                      className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-zinc-900 transition-colors shrink-0"
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                )}

                {!slug && (
                  <p className="text-xs text-amber-600">
                    Set an email address above to start receiving invoices.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Template Manager — CRUD for invoice templates
// ---------------------------------------------------------------------------

const FONT_OPTIONS = [
  { value: 'Helvetica', label: 'Helvetica' },
  { value: 'Times-Roman', label: 'Times Roman' },
  { value: 'Courier', label: 'Courier' },
];

const TemplateManager = ({ company }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // template object or 'new'
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await apiFetch('/api/invoice-templates', { companyId: company.companyId });
      const data = await res.json();
      setTemplates(data.templates || []);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [company.companyId]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await apiFetch(`/api/invoice-templates/${id}`, { method: 'DELETE', companyId: company.companyId });
      fetchTemplates();
    } catch (err) { setError(err.message); }
  };

  const handleDuplicate = async (id) => {
    try {
      await apiFetch(`/api/invoice-templates/${id}/duplicate`, { method: 'POST', companyId: company.companyId });
      fetchTemplates();
    } catch (err) { setError(err.message); }
  };

  const handleSave = async (formData) => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      if (editing && editing !== 'new' && editing.id) {
        await apiFetch(`/api/invoice-templates/${editing.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData),
          companyId: company.companyId,
        });
      } else {
        await apiFetch('/api/invoice-templates', {
          method: 'POST',
          body: JSON.stringify(formData),
          companyId: company.companyId,
        });
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      setEditing(null);
      fetchTemplates();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    const isNew = editing === 'new';
    const initial = isNew ? { name: '', is_default: templates.length === 0, template_config: { ...DEFAULT_TEMPLATE_CONFIG } } : editing;
    return (
      <TemplateEditor
        template={initial}
        isNew={isNew}
        onSave={handleSave}
        onCancel={() => { setEditing(null); setError(''); }}
        saving={saving}
        error={error}
        saved={saved}
      />
    );
  }

  return (
    <div className="bg-white border border-zinc-100 rounded-lg overflow-hidden">
      <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-zinc-100 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-medium text-zinc-900">Invoice Templates</h2>
          <p className="text-[11px] text-zinc-400 mt-0.5">Customize the appearance of your invoices</p>
        </div>
        <button
          onClick={() => setEditing('new')}
          className="px-3 py-1.5 text-xs bg-zinc-900 text-white rounded-md hover:bg-zinc-800 transition-colors flex items-center gap-1.5"
        >
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 3v10M3 8h10" /></svg>
          New Template
        </button>
      </div>

      <div className="p-4 sm:p-5">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-4 h-4 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" />
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-zinc-400 mb-3">No templates yet. Create one to customize your invoice appearance.</p>
            <button
              onClick={() => setEditing('new')}
              className="px-4 py-2 text-sm border border-zinc-200 rounded-md hover:border-zinc-400 transition-colors text-zinc-600"
            >
              Create your first template
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {templates.map((t) => (
              <div key={t.id} className="flex items-center justify-between px-4 py-3 rounded-lg border border-zinc-200 hover:border-zinc-300 transition-colors group">
                <div className="flex items-center gap-3 min-w-0">
                  {/* Color swatch */}
                  <div
                    className="w-8 h-8 rounded-md border border-zinc-200 shrink-0"
                    style={{ background: `linear-gradient(135deg, ${t.template_config?.colors?.primary || '#18181b'} 50%, ${t.template_config?.colors?.accent || '#f4f4f5'} 50%)` }}
                  />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-zinc-900 truncate">{t.name}</span>
                      {t.is_default && (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-500 font-medium shrink-0">Default</span>
                      )}
                    </div>
                    <p className="text-[11px] text-zinc-400">
                      {t.template_config?.font_family || 'Helvetica'}
                      {t.template_config?.company_name_override && ` · ${t.template_config.company_name_override}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <button onClick={() => setEditing(t)} className="p-1.5 text-zinc-400 hover:text-zinc-700 transition-colors rounded-md hover:bg-zinc-50" title="Edit">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M13.5 2.5l-1-1-9 9-.5 2.5 2.5-.5 9-9-1-1Z" /></svg>
                  </button>
                  <button onClick={() => handleDuplicate(t.id)} className="p-1.5 text-zinc-400 hover:text-zinc-700 transition-colors rounded-md hover:bg-zinc-50" title="Duplicate">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="5" y="5" width="9" height="9" rx="1" /><path d="M5 11H3a1 1 0 01-1-1V3a1 1 0 011-1h7a1 1 0 011 1v2" /></svg>
                  </button>
                  <button onClick={() => handleDelete(t.id)} className="p-1.5 text-zinc-400 hover:text-red-500 transition-colors rounded-md hover:bg-zinc-50" title="Delete">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 5h10M5.5 5V3.5a1 1 0 011-1h3a1 1 0 011 1V5M6.5 7.5v4M9.5 7.5v4" /><path d="M4 5l.5 8a1.5 1.5 0 001.5 1.5h4a1.5 1.5 0 001.5-1.5L12 5" /></svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {error && <p className="text-sm text-red-500 mt-3">{error}</p>}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Template Editor — form for creating/editing a template
// ---------------------------------------------------------------------------

const TemplateEditor = ({ template, isNew, onSave, onCancel, saving, error, saved }) => {
  const [name, setName] = useState(template.name || '');
  const [isDefault, setIsDefault] = useState(template.is_default || false);
  const config = template.template_config || DEFAULT_TEMPLATE_CONFIG;
  const [companyNameOverride, setCompanyNameOverride] = useState(config.company_name_override || '');
  const [companyAddress, setCompanyAddress] = useState(config.company_address || '');
  const [companyPhone, setCompanyPhone] = useState(config.company_phone || '');
  const [companyEmail, setCompanyEmail] = useState(config.company_email || '');
  const [companyWebsite, setCompanyWebsite] = useState(config.company_website || '');
  const [companyTaxId, setCompanyTaxId] = useState(config.company_tax_id || '');
  const [primaryColor, setPrimaryColor] = useState(config.colors?.primary || '#18181b');
  const [accentColor, setAccentColor] = useState(config.colors?.accent || '#f4f4f5');
  const [textColor, setTextColor] = useState(config.colors?.text || '#27272a');
  const [bgColor, setBgColor] = useState(config.colors?.background || '#ffffff');
  const [fontFamily, setFontFamily] = useState(config.font_family || 'Helvetica');
  const [showCompanyDetails, setShowCompanyDetails] = useState(config.show_company_details !== false);
  const [showNotes, setShowNotes] = useState(config.show_notes_section !== false);
  const [showFooter, setShowFooter] = useState(config.show_footer_section !== false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      name: name.trim() || 'Untitled',
      is_default: isDefault,
      template_config: {
        company_name_override: companyNameOverride.trim() || null,
        company_address: companyAddress.trim(),
        company_phone: companyPhone.trim(),
        company_email: companyEmail.trim(),
        company_website: companyWebsite.trim(),
        company_tax_id: companyTaxId.trim(),
        colors: { primary: primaryColor, accent: accentColor, text: textColor, background: bgColor },
        font_family: fontFamily,
        show_company_details: showCompanyDetails,
        show_notes_section: showNotes,
        show_footer_section: showFooter,
      },
    });
  };

  const inputCls = 'w-full px-3 py-2 border border-zinc-200 text-sm text-zinc-900 rounded-md outline-none focus:border-zinc-900 transition-colors duration-300';
  const labelCls = 'block text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-1.5';

  return (
    <div className="bg-white border border-zinc-100 rounded-lg overflow-hidden">
      <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-zinc-100 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-medium text-zinc-900">{isNew ? 'New Template' : `Edit "${template.name}"`}</h2>
          <p className="text-[11px] text-zinc-400 mt-0.5">Customize your invoice appearance</p>
        </div>
        <button onClick={onCancel} className="text-sm text-zinc-500 hover:text-zinc-900 transition-colors">Back to list</button>
      </div>

      <form onSubmit={handleSubmit} className="p-4 sm:p-5 space-y-6">
        {/* Name + Default */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Template Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Professional, Minimal" className={inputCls} required />
          </div>
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900" />
              <span className="text-sm text-zinc-700">Set as default template</span>
            </label>
          </div>
        </div>

        {/* Company Info */}
        <div>
          <h3 className="text-xs font-semibold text-zinc-700 uppercase tracking-wider mb-3 flex items-center gap-2">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-400"><rect x="2" y="3" width="12" height="11" rx="1" /><path d="M5 3V1.5M11 3V1.5M2 6.5h12" /></svg>
            Company Info
          </h3>
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Company Name Override</label>
              <input type="text" value={companyNameOverride} onChange={(e) => setCompanyNameOverride(e.target.value)} placeholder="Leave blank to use company name" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Address</label>
              <input type="text" value={companyAddress} onChange={(e) => setCompanyAddress(e.target.value)} placeholder="123 Main St, City, ST 12345" className={inputCls} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Phone</label>
                <input type="text" value={companyPhone} onChange={(e) => setCompanyPhone(e.target.value)} placeholder="(555) 123-4567" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input type="email" value={companyEmail} onChange={(e) => setCompanyEmail(e.target.value)} placeholder="billing@company.com" className={inputCls} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Website</label>
                <input type="text" value={companyWebsite} onChange={(e) => setCompanyWebsite(e.target.value)} placeholder="www.company.com" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Tax ID</label>
                <input type="text" value={companyTaxId} onChange={(e) => setCompanyTaxId(e.target.value)} placeholder="XX-XXXXXXX" className={inputCls} />
              </div>
            </div>
          </div>
        </div>

        {/* Colors */}
        <div>
          <h3 className="text-xs font-semibold text-zinc-700 uppercase tracking-wider mb-3 flex items-center gap-2">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-400"><circle cx="8" cy="8" r="6" /><path d="M8 2a6 6 0 010 12" fill="currentColor" opacity=".15" /></svg>
            Colors
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Primary', value: primaryColor, set: setPrimaryColor },
              { label: 'Accent', value: accentColor, set: setAccentColor },
              { label: 'Text', value: textColor, set: setTextColor },
              { label: 'Background', value: bgColor, set: setBgColor },
            ].map((c) => (
              <div key={c.label}>
                <label className={labelCls}>{c.label}</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={c.value}
                    onChange={(e) => c.set(e.target.value)}
                    className="w-8 h-8 rounded border border-zinc-200 cursor-pointer p-0.5"
                  />
                  <input
                    type="text"
                    value={c.value}
                    onChange={(e) => c.set(e.target.value)}
                    className="flex-1 px-2 py-1.5 border border-zinc-200 text-xs font-mono text-zinc-700 rounded-md outline-none focus:border-zinc-900 transition-colors"
                    maxLength={7}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Font */}
        <div>
          <h3 className="text-xs font-semibold text-zinc-700 uppercase tracking-wider mb-3 flex items-center gap-2">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-400"><path d="M3 13h10M5 3h6l-3 10" /></svg>
            Font
          </h3>
          <select value={fontFamily} onChange={(e) => setFontFamily(e.target.value)} className={inputCls + ' bg-white appearance-none cursor-pointer'}>
            {FONT_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </div>

        {/* Visibility Toggles */}
        <div>
          <h3 className="text-xs font-semibold text-zinc-700 uppercase tracking-wider mb-3 flex items-center gap-2">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-400"><path d="M1 8s3-5 7-5 7 5 7 5-3 5-7 5-7-5-7-5z" /><circle cx="8" cy="8" r="2" /></svg>
            Section Visibility
          </h3>
          <div className="space-y-2">
            {[
              { label: 'Show company details (address, phone, etc.)', checked: showCompanyDetails, set: setShowCompanyDetails },
              { label: 'Show notes section', checked: showNotes, set: setShowNotes },
              { label: 'Show footer/terms section', checked: showFooter, set: setShowFooter },
            ].map((toggle) => (
              <label key={toggle.label} className="flex items-center gap-2.5 cursor-pointer py-1">
                <input type="checkbox" checked={toggle.checked} onChange={(e) => toggle.set(e.target.checked)} className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900" />
                <span className="text-sm text-zinc-700">{toggle.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Preview swatch */}
        <div>
          <h3 className="text-xs font-semibold text-zinc-700 uppercase tracking-wider mb-3">Preview</h3>
          <div className="rounded-lg border border-zinc-200 overflow-hidden" style={{ backgroundColor: bgColor }}>
            <div className="p-4" style={{ fontFamily: fontFamily === 'Times-Roman' ? 'Times New Roman, serif' : fontFamily === 'Courier' ? 'Courier New, monospace' : 'Helvetica, Arial, sans-serif' }}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="font-bold text-base" style={{ color: primaryColor }}>{companyNameOverride || 'Company Name'}</div>
                  {showCompanyDetails && companyAddress && <div className="text-[10px]" style={{ color: textColor }}>{companyAddress}</div>}
                  {showCompanyDetails && companyPhone && <div className="text-[10px]" style={{ color: textColor }}>{companyPhone}</div>}
                </div>
                <div className="font-bold text-xl" style={{ color: primaryColor }}>INVOICE</div>
              </div>
              <div className="rounded" style={{ backgroundColor: accentColor }}>
                <div className="flex px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide" style={{ color: textColor }}>
                  <span className="flex-[3]">Description</span>
                  <span className="flex-1 text-right">Qty</span>
                  <span className="flex-1 text-right">Price</span>
                  <span className="flex-1 text-right">Amount</span>
                </div>
              </div>
              <div className="flex px-3 py-1.5 text-xs" style={{ color: textColor }}>
                <span className="flex-[3]">Sample item</span>
                <span className="flex-1 text-right">2</span>
                <span className="flex-1 text-right">$150.00</span>
                <span className="flex-1 text-right font-semibold">$300.00</span>
              </div>
            </div>
          </div>
        </div>

        {error && <p className="text-sm text-red-500">{error}</p>}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={saving || !name.trim()}
            className="px-5 py-2 bg-zinc-900 text-white text-xs tracking-wider uppercase rounded-md hover:bg-zinc-800 transition-colors disabled:opacity-40 flex items-center gap-2"
          >
            {saving && <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />}
            {saving ? 'Saving...' : isNew ? 'Create Template' : 'Save Changes'}
          </button>
          <button type="button" onClick={onCancel} className="px-4 py-2 text-xs text-zinc-500 hover:text-zinc-900 transition-colors">
            Cancel
          </button>
          {saved && <span className="text-sm text-emerald-600">Saved</span>}
        </div>
      </form>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Invoicing settings — column layout configurator
// ---------------------------------------------------------------------------

const DEFAULT_COLUMNS = [
  { key: 'description', label: 'Description', type: 'text', required: true },
  { key: 'quantity', label: 'Quantity', type: 'number', default: 1 },
  { key: 'unit_price', label: 'Unit Price', type: 'currency', default: 0 },
  { key: 'amount', label: 'Amount', type: 'computed', formula: 'quantity * unit_price' },
];

const INV_COLUMN_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
  { value: 'percent', label: 'Percent' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Checkbox' },
];

const invSlugify = (label) =>
  label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || `col_${Date.now()}`;

const InvoicingSettings = ({ company, onUpdated }) => {
  const formulaRef = useRef(null);
  const newNameRef = useRef(null);
  const settings = company.company?.invoice_settings || {};
  const [columns, setColumns] = useState(settings.line_columns || DEFAULT_COLUMNS);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');

  const original = JSON.stringify(settings.line_columns || DEFAULT_COLUMNS);
  const hasChanges = JSON.stringify(columns) !== original;

  const midCols = columns.slice(1, -1);
  const amountCol = columns[columns.length - 1];

  const updateMid = (idx, field, value) => {
    const updated = [...midCols];
    updated[idx] = { ...updated[idx], [field]: value };
    if (field === 'label' && !updated[idx]._keyEdited) {
      updated[idx].key = invSlugify(value);
    }
    setColumns([columns[0], ...updated, amountCol]);
  };

  const moveMid = (idx, dir) => {
    const updated = [...midCols];
    const target = idx + dir;
    if (target < 0 || target >= updated.length) return;
    [updated[idx], updated[target]] = [updated[target], updated[idx]];
    setColumns([columns[0], ...updated, amountCol]);
  };

  const removeMid = (idx) => {
    const updated = midCols.filter((_, i) => i !== idx);
    setColumns([columns[0], ...updated, amountCol]);
  };

  const commitAdd = () => {
    const label = newName.trim();
    if (!label) return;
    const key = invSlugify(label);
    const updated = [...midCols, { key, label, type: 'number', default: 0 }];
    setColumns([columns[0], ...updated, amountCol]);
    setNewName('');
    setAdding(false);
  };

  const startAdd = () => {
    setAdding(true);
    setNewName('');
    requestAnimationFrame(() => newNameRef.current?.focus());
  };

  const cancelAdd = () => {
    setNewName('');
    setAdding(false);
  };

  const insertVar = (key) => {
    const input = formulaRef.current;
    if (!input) {
      updateFormula((amountCol.formula || '') + key);
      return;
    }
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const current = amountCol.formula || '';
    const before = current.slice(0, start);
    const after = current.slice(end);
    const needSpaceBefore = before.length > 0 && !/[\s(+\-*/]$/.test(before);
    const needSpaceAfter = after.length > 0 && !/^[\s)+\-*/]/.test(after);
    const insert = (needSpaceBefore ? ' ' : '') + key + (needSpaceAfter ? ' ' : '');
    updateFormula(before + insert + after);
    requestAnimationFrame(() => {
      const pos = start + insert.length;
      input.focus();
      input.setSelectionRange(pos, pos);
    });
  };

  const formulaVars = columns.filter((c) => c.type !== 'computed' && c.type !== 'text' && c.type !== 'boolean' && c.type !== 'date' && c.label?.trim());

  const updateFormula = (formula) => {
    setColumns([...columns.slice(0, -1), { ...amountCol, formula }]);
  };

  const updateDescLabel = (label) => {
    setColumns([{ ...columns[0], label }, ...columns.slice(1)]);
  };

  const updateAmountLabel = (label) => {
    setColumns([...columns.slice(0, -1), { ...amountCol, label }]);
  };

  const handleReset = () => {
    setColumns(DEFAULT_COLUMNS);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const updatedSettings = { ...settings, line_columns: columns };
      const res = await apiFetch('/api/companies', {
        method: 'PUT',
        body: JSON.stringify({ invoice_settings: updatedSettings }),
        companyId: company.companyId,
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save');
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      if (onUpdated) onUpdated();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Sample data for live preview
  const sampleValues = {};
  for (const col of columns) {
    if (col.type === 'computed') continue;
    if (col.type === 'number') sampleValues[col.key] = col.key === 'quantity' ? 2 : 1;
    else if (col.type === 'currency') sampleValues[col.key] = col.key === 'unit_price' ? 150 : 100;
    else if (col.type === 'percent') sampleValues[col.key] = 10;
    else if (col.type === 'boolean') sampleValues[col.key] = true;
    else if (col.type === 'date') sampleValues[col.key] = '2026-01-15';
    else sampleValues[col.key] = col.key === 'description' ? 'Sample service' : 'Sample';
  }

  // Compute amount for preview
  let sampleAmount = 0;
  if (amountCol?.formula) {
    const keys = Object.keys(sampleValues);
    const vals = Object.values(sampleValues).map(v => Number(v) || 0);
    try {
      // eslint-disable-next-line no-new-func
      const fn = new Function(...keys, `"use strict"; return (${amountCol.formula});`);
      const result = fn(...vals);
      sampleAmount = isFinite(result) ? Math.round(result * 100) / 100 : 0;
    } catch { sampleAmount = 0; }
  }

  const fmt = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  return (
    <div className="bg-white border border-zinc-100 rounded-lg overflow-hidden">
      <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-zinc-100">
        <h2 className="text-sm font-medium text-zinc-900">Invoice Column Layout</h2>
        <p className="text-[11px] text-zinc-400 mt-0.5">Configure the default columns for your invoice line items</p>
      </div>

      <div className="p-4 sm:p-5 space-y-5">
        {/* Column editor */}
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-medium mb-2">Columns</label>
          <div className="border border-zinc-300 rounded-lg overflow-hidden">
            <div className="bg-zinc-50 p-3 space-y-1">
              {/* Description (locked) */}
              <div className="flex items-center gap-3 px-3 py-2 rounded-md bg-white border border-zinc-200">
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-400 shrink-0"><rect x="3" y="7" width="10" height="7" rx="1.5" /><path d="M5 7V5a3 3 0 016 0v2" /></svg>
                <input type="text" value={columns[0].label} onChange={(e) => updateDescLabel(e.target.value)}
                  className="flex-1 text-sm font-medium bg-transparent outline-none text-zinc-800 placeholder-zinc-400" placeholder="Label" />
                <span className="text-[10px] text-zinc-500 font-mono bg-zinc-100 px-1.5 py-0.5 rounded shrink-0">text</span>
              </div>

              {/* Middle columns */}
              {midCols.map((col, idx) => (
                <div key={col.key} className="flex items-center gap-3 px-3 py-2 rounded-md bg-white border border-zinc-200 group">
                  <div className="flex flex-col shrink-0 -my-1">
                    <button type="button" onClick={() => moveMid(idx, -1)} disabled={idx === 0}
                      className="text-zinc-400 hover:text-zinc-700 disabled:text-zinc-200 transition-colors" title="Move up">
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 10l4-4 4 4" /></svg>
                    </button>
                    <button type="button" onClick={() => moveMid(idx, 1)} disabled={idx === midCols.length - 1}
                      className="text-zinc-400 hover:text-zinc-700 disabled:text-zinc-200 transition-colors" title="Move down">
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 6l4 4 4-4" /></svg>
                    </button>
                  </div>
                  <input
                    type="text"
                    value={col.label}
                    onChange={(e) => updateMid(idx, 'label', e.target.value)}
                    className="flex-1 text-sm font-medium bg-transparent outline-none min-w-0 text-zinc-800 placeholder-zinc-400"
                    placeholder="Column name"
                  />
                  <select
                    value={col.type}
                    onChange={(e) => updateMid(idx, 'type', e.target.value)}
                    className="text-xs text-zinc-600 bg-zinc-100 border border-zinc-200 rounded px-2 py-1 outline-none cursor-pointer shrink-0"
                  >
                    {INV_COLUMN_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <button type="button" onClick={() => removeMid(idx)}
                    className="text-zinc-300 hover:text-red-500 group-hover:text-zinc-400 transition-colors shrink-0" title="Remove">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 4l8 8M12 4l-8 8" /></svg>
                  </button>
                </div>
              ))}

              {/* Add column */}
              {adding ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-white border-2 border-zinc-400">
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" className="text-zinc-400 shrink-0"><path d="M8 3v10M3 8h10" /></svg>
                  <input
                    ref={newNameRef}
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); commitAdd(); }
                      if (e.key === 'Escape') cancelAdd();
                    }}
                    onBlur={() => { if (!newName.trim()) cancelAdd(); }}
                    className="flex-1 text-sm font-medium bg-transparent outline-none text-zinc-800 placeholder-zinc-400 min-w-0"
                    placeholder="Column name, then press Enter"
                  />
                  <button type="button" onClick={commitAdd} disabled={!newName.trim()}
                    className="text-zinc-400 hover:text-emerald-600 disabled:text-zinc-200 transition-colors shrink-0" title="Add">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 8l4 4 6-6" /></svg>
                  </button>
                  <button type="button" onClick={cancelAdd}
                    className="text-zinc-400 hover:text-zinc-700 transition-colors shrink-0" title="Cancel">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 4l8 8M12 4l-8 8" /></svg>
                  </button>
                </div>
              ) : (
                <button type="button" onClick={startAdd}
                  className="flex items-center gap-2 px-3 py-2 w-full text-sm text-zinc-500 hover:text-zinc-800 hover:bg-white rounded-md border border-dashed border-zinc-300 hover:border-zinc-400 transition-colors">
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 3v10M3 8h10" /></svg>
                  Add column
                </button>
              )}
            </div>

            {/* Formula section */}
            <div className="border-t border-zinc-300 bg-white px-3 py-3 space-y-2">
              <div className="flex items-center gap-2">
                <input type="text" value={amountCol.label} onChange={(e) => updateAmountLabel(e.target.value)}
                  className="w-20 text-sm font-semibold text-zinc-800 bg-transparent outline-none border-b border-transparent focus:border-zinc-400 transition-colors" />
                <span className="text-sm text-zinc-400 font-medium">=</span>
                <input
                  ref={formulaRef}
                  type="text"
                  value={amountCol.formula || ''}
                  onChange={(e) => updateFormula(e.target.value)}
                  className="flex-1 text-sm font-mono text-zinc-800 bg-zinc-50 rounded-md px-3 py-1.5 outline-none border border-zinc-200 focus:border-zinc-500 focus:ring-1 focus:ring-zinc-200 transition-all"
                  placeholder="e.g. quantity * unit_price"
                />
              </div>
              {formulaVars.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] text-zinc-400 uppercase tracking-wider mr-0.5">Variables:</span>
                  {formulaVars.map((v) => (
                    <button
                      key={v.key}
                      type="button"
                      onClick={() => insertVar(v.key)}
                      className="px-2 py-0.5 text-xs font-mono bg-zinc-100 hover:bg-zinc-900 hover:text-white text-zinc-700 rounded-md transition-colors border border-zinc-200 hover:border-zinc-900"
                      title={`Insert "${v.key}" into formula`}
                    >
                      {v.key}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Live preview */}
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-2">Preview</label>
          <div className="border border-zinc-200 rounded-md overflow-hidden">
            <div className="flex bg-zinc-50 border-b border-zinc-200 px-3 py-2">
              {columns.map((col) => (
                <div key={col.key} className={`text-[10px] font-medium text-zinc-500 uppercase tracking-wider ${col.type === 'text' ? 'flex-[3]' : 'flex-1 text-right'}`}>
                  {col.label}
                </div>
              ))}
            </div>
            <div className="flex px-3 py-2.5">
              {columns.map((col) => (
                <div key={col.key} className={`text-sm ${col.type === 'text' ? 'flex-[3] text-zinc-900' : 'flex-1 text-right text-zinc-600'}`}>
                  {col.type === 'computed' ? fmt(sampleAmount)
                    : col.type === 'currency' ? fmt(sampleValues[col.key])
                    : col.type === 'percent' ? `${sampleValues[col.key]}%`
                    : col.type === 'boolean' ? (sampleValues[col.key] ? 'Yes' : 'No')
                    : col.type === 'date' ? 'Jan 15, 2026'
                    : sampleValues[col.key]}
                </div>
              ))}
            </div>
          </div>
        </div>

        {error && <p className="text-sm text-red-500">{error}</p>}

        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="px-5 py-2 bg-zinc-900 text-white text-xs tracking-wider uppercase rounded-md hover:bg-zinc-800 transition-colors disabled:opacity-40 flex items-center gap-2"
          >
            {saving && (
              <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
            )}
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={handleReset}
            disabled={JSON.stringify(columns) === JSON.stringify(DEFAULT_COLUMNS)}
            className="px-4 py-2 text-xs text-zinc-500 hover:text-zinc-900 transition-colors disabled:opacity-30"
          >
            Reset to default
          </button>
          {saved && (
            <span className="text-sm text-emerald-600">Saved</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsView;
